import Dexie, { type EntityTable } from 'dexie';

/** One directory row from GET /api/v2/public-organisations/ items[]. */
export interface TrainingCentreAccreditationLevelRow {
  id: number;
  name_en: string;
  name_tet: string;
  name_pt: string;
  sort_order: number | null;
}

export type TrainingCentreLocationKind =
  | 'unspecified'
  | 'point'
  | 'district'
  | 'subdistrict'
  | 'suco';

/** District, subdistrict, or suco from the API (ids are DB primary keys). */
export interface TrainingCentreLocationUnit {
  id: number;
  code: number;
  name: string;
}

export interface TrainingCentreLocationTimor {
  district: TrainingCentreLocationUnit | null;
  subdistrict: TrainingCentreLocationUnit | null;
  suco: TrainingCentreLocationUnit | null;
}

export interface TrainingCentreLocationRow {
  id: number;
  name: string;
  type: TrainingCentreLocationKind;
  /** Map pin: explicit point or point-on-surface of selected area. */
  point: { lat: number; lng: number } | null;
  /** Present when type is `point` and coordinates are set (contains-queries). */
  inferred?: TrainingCentreLocationTimor;
  /** Present when type is district / subdistrict / suco. */
  area?: TrainingCentreLocationTimor;
}

/** Certificate tier on `Program` (GET API `program.certificate_level`); distinct from link accreditation. */
export interface ProgramLevelRow {
  id: number;
  sort_order: number | null;
  /** I–IV when set on canonical certificate tier rows; labels come from i18n. */
  roman_numeral?: string;
}

/** Matches program competencies in public API program detail / list `program` object. */
export interface ProgramCompetencyRow {
  id: number;
  name: string;
  /** Present from API v2; optional for older IndexedDB rows. */
  name_en?: string;
  name_pt?: string;
  kodigu: string;
  hours: number | null;
}

/** Program identity from the API (`programs[].program` and the `program` Dexie table). */
export interface ProgramRow {
  id: number;
  name: string;
  kodigu: string;
  /** Certificate tier (I–III) from Programatiku import (`ProgramLevel`). */
  certificate_level: ProgramLevelRow | null;
  /** From GET /api/v2/public-organisations/ nested program (for offline program detail). */
  competencies?: ProgramCompetencyRow[];
  /** Wagtail mirror page for this program in the current locale (when present). */
  detail_page_id?: number;
  detail_url_path?: string;
}

/** List/card heading: name plus certificate roman numeral, space-separated (no brackets). */
export function programRowCardTitle(prog: ProgramRow): string {
  const name = (prog.name || '').trim();
  if (!name) return '';
  const r = (prog.certificate_level?.roman_numeral || '').trim();
  if (r) return `${name} ${r}`;
  return name;
}

/** Tier for a training-centre–program accreditation (roman or classification letter). */
export interface ProgramAccreditationLevelRow {
  id: number;
  sort_order: number | null;
  /** Roman I–IV or klasifikasaun letter; labels from i18n. */
  roman_numeral?: string;
}

/** One program link from GET /api/v2/public-organisations/ items[].programs[]. */
export interface TrainingCentreProgramLinkRow {
  program: ProgramRow;
  numeru_akreditasaun_programa: string;
  komentariu: string;
  data: string | null;
  data_remata: string | null;
  accreditation_level: ProgramAccreditationLevelRow | null;
}

/**
 * Roman numeral for list labels: prefer the program’s certificate tier, then the link’s
 * program-accreditation tier (matches API `certificate_level` / `accreditation_level`).
 */
export function trainingCentreProgramLinkRomanNumeral(link: TrainingCentreProgramLinkRow): string {
  const fromCert = (link.program?.certificate_level?.roman_numeral || '').trim();
  if (fromCert) return fromCert;
  return (link.accreditation_level?.roman_numeral || '').trim();
}

/** One line for cards/lists: program name plus tier roman numeral, space-separated (no brackets). */
export function trainingCentreProgramLinkListLabel(link: TrainingCentreProgramLinkRow): string {
  const name = (link.program?.name || '').trim();
  if (!name) return '';
  const r = trainingCentreProgramLinkRomanNumeral(link);
  if (r) return `${name} ${r}`;
  return name;
}

/**
 * Stable key so the same program name at different certificate / accreditation tiers stays
 * distinct (does not collapse on name only).
 */
export function trainingCentreProgramLinkDedupeKey(link: TrainingCentreProgramLinkRow): string {
  const p = link.program;
  const pid = p?.id ?? 0;
  const certId = p?.certificate_level?.id ?? '∅';
  const accId = link.accreditation_level?.id ?? '∅';
  return `${pid}|${certId}|${accId}`;
}

/** Sorted labels for one training centre (distinct offerings). */
export function distinctTrainingCentreProgramListLabels(centre: TrainingCentreRow): string[] {
  const byKey = new Map<string, string>();
  for (const link of centre.programs ?? []) {
    const label = trainingCentreProgramLinkListLabel(link);
    if (!label) continue;
    const key = trainingCentreProgramLinkDedupeKey(link);
    if (!byKey.has(key)) byKey.set(key, label);
  }
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/** Sentinel for “all” in location admin filters (district / subdistrict `<select>`). */
export const TRAINING_CENTRE_LOCATION_FILTER_ALL = '__all__';

/** District name from API ``area`` / ``inferred`` (legacy display / sorting by name). */
export function trainingCentreLocationDistrictKey(loc: TrainingCentreLocationRow): string {
  const t = loc.area ?? loc.inferred;
  return (t?.district?.name || '').trim();
}

export function trainingCentreLocationTimor(
  loc: TrainingCentreLocationRow
): TrainingCentreLocationTimor | undefined {
  return loc.area ?? loc.inferred;
}

export function trainingCentreLocationDistrictUnit(
  loc: TrainingCentreLocationRow
): TrainingCentreLocationUnit | null {
  const d = trainingCentreLocationTimor(loc)?.district;
  return d != null && typeof d.id === 'number' ? d : null;
}

export function trainingCentreLocationSubdistrictUnit(
  loc: TrainingCentreLocationRow
): TrainingCentreLocationUnit | null {
  const s = trainingCentreLocationTimor(loc)?.subdistrict;
  return s != null && typeof s.id === 'number' ? s : null;
}

/** Stable `<option value>` for district filter (PK from API). */
export function trainingCentreLocationDistrictFilterValue(loc: TrainingCentreLocationRow): string | null {
  const d = trainingCentreLocationDistrictUnit(loc);
  return d ? `district:${d.id}` : null;
}

/** Stable `<option value>` for subdistrict filter (PK from API). */
export function trainingCentreLocationSubdistrictFilterValue(loc: TrainingCentreLocationRow): string | null {
  const s = trainingCentreLocationSubdistrictUnit(loc);
  return s ? `subdistrict:${s.id}` : null;
}

export type ParsedTrainingCentreLocationAdminFilter =
  | { kind: 'all' }
  | { kind: 'district'; id: number }
  | { kind: 'subdistrict'; id: number };

export function parseTrainingCentreLocationAdminFilterValue(
  raw: string
): ParsedTrainingCentreLocationAdminFilter {
  if (!raw || raw === TRAINING_CENTRE_LOCATION_FILTER_ALL) return { kind: 'all' };
  if (raw.startsWith('district:')) {
    const id = Number(raw.slice('district:'.length));
    if (Number.isFinite(id)) return { kind: 'district', id };
  }
  if (raw.startsWith('subdistrict:')) {
    const id = Number(raw.slice('subdistrict:'.length));
    if (Number.isFinite(id)) return { kind: 'subdistrict', id };
  }
  return { kind: 'all' };
}

/**
 * Whether ``loc`` matches the current district / subdistrict filter selection.
 * Subdistrict filter is strict (location must have that subdistrict). District-only
 * filter matches any location in that district.
 */
export function trainingCentreLocationMatchesAdminFilters(
  loc: TrainingCentreLocationRow,
  selectedDistrictValue: string,
  selectedSubdistrictValue: string,
): boolean {
  const sub = parseTrainingCentreLocationAdminFilterValue(selectedSubdistrictValue);
  if (sub.kind === 'subdistrict') {
    const su = trainingCentreLocationSubdistrictUnit(loc);
    if (!su || su.id !== sub.id) return false;
    const dist = parseTrainingCentreLocationAdminFilterValue(selectedDistrictValue);
    if (dist.kind === 'district') {
      const du = trainingCentreLocationDistrictUnit(loc);
      if (!du || du.id !== dist.id) return false;
    }
    return true;
  }
  const dist = parseTrainingCentreLocationAdminFilterValue(selectedDistrictValue);
  if (dist.kind === 'district') {
    const du = trainingCentreLocationDistrictUnit(loc);
    return Boolean(du && du.id === dist.id);
  }
  return true;
}

export function trainingCentreRowMatchesLocationAdminFilters(
  centre: TrainingCentreRow,
  selectedDistrictValue: string,
  selectedSubdistrictValue: string
): boolean {
  const d = parseTrainingCentreLocationAdminFilterValue(selectedDistrictValue);
  const s = parseTrainingCentreLocationAdminFilterValue(selectedSubdistrictValue);
  if (d.kind === 'all' && s.kind === 'all') return true;
  const locs = centre.locations ?? [];
  return locs.some((loc) =>
    trainingCentreLocationMatchesAdminFilters(loc, selectedDistrictValue, selectedSubdistrictValue)
  );
}

/** True if some centre that offers ``programId`` has a location matching the admin filters. */
export function programRowOfferedWithLocationAdminFilters(
  centres: readonly TrainingCentreRow[],
  programId: number,
  selectedDistrictValue: string,
  selectedSubdistrictValue: string
): boolean {
  const d = parseTrainingCentreLocationAdminFilterValue(selectedDistrictValue);
  const s = parseTrainingCentreLocationAdminFilterValue(selectedSubdistrictValue);
  if (d.kind === 'all' && s.kind === 'all') {
    return centres.some((c) => (c.programs ?? []).some((link) => link.program?.id === programId));
  }
  for (const centre of centres) {
    const hasProg = (centre.programs ?? []).some((link) => link.program?.id === programId);
    if (!hasProg) continue;
    if (
      trainingCentreRowMatchesLocationAdminFilters(
        centre,
        selectedDistrictValue,
        selectedSubdistrictValue
      )
    ) {
      return true;
    }
  }
  return false;
}

export interface TrainingCentreLocationFilterOption {
  value: string;
  label: string;
}

/** Distinct district options from all locations on ``centres`` (stable value + display label). */
export function collectDistrictFilterOptionsFromCentres(
  centres: readonly TrainingCentreRow[]
): TrainingCentreLocationFilterOption[] {
  const byValue = new Map<string, string>();
  for (const c of centres) {
    for (const loc of c.locations ?? []) {
      const v = trainingCentreLocationDistrictFilterValue(loc);
      const unit = trainingCentreLocationDistrictUnit(loc);
      if (!v || !unit) continue;
      if (!byValue.has(v)) {
        const label = (unit.name || '').trim();
        byValue.set(v, label || v);
      }
    }
  }
  return [...byValue.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/** Subdistrict options for a selected district filter value; empty if district is “all”. */
export function collectSubdistrictFilterOptionsForDistrict(
  centres: readonly TrainingCentreRow[],
  selectedDistrictValue: string
): TrainingCentreLocationFilterOption[] {
  const parsed = parseTrainingCentreLocationAdminFilterValue(selectedDistrictValue);
  if (parsed.kind !== 'district') return [];
  const districtId = parsed.id;
  const byValue = new Map<string, { label: string; code: number }>();
  for (const c of centres) {
    for (const loc of c.locations ?? []) {
      const du = trainingCentreLocationDistrictUnit(loc);
      if (!du || du.id !== districtId) continue;
      const su = trainingCentreLocationSubdistrictUnit(loc);
      if (!su) continue;
      const v = trainingCentreLocationSubdistrictFilterValue(loc);
      if (!v) continue;
      if (!byValue.has(v)) {
        byValue.set(v, { label: (su.name || '').trim() || v, code: Number(su.code) || 0 });
      }
    }
  }
  return [...byValue.entries()]
    .map(([value, meta]) => ({ value, label: meta.label, code: meta.code }))
    .sort((a, b) => {
      if (a.code !== b.code) return a.code - b.code;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    })
    .map(({ value, label }) => ({ value, label }));
}

/** District, subdistrict, and suco as one line (coarse → fine), from ``area`` or point ``inferred``. */
export function trainingCentreLocationHierarchyLine(loc: TrainingCentreLocationRow): string {
  const t = loc.area ?? loc.inferred;
  if (!t) return '';
  const parts = [t.district?.name, t.subdistrict?.name, t.suco?.name].filter(
    (x): x is string => typeof x === 'string' && Boolean(x.trim())
  );
  return parts.join(', ');
}

/**
 * One-line label for cards and lists: optional free-text ``name`` plus Timor hierarchy when known.
 */
export function trainingCentreLocationLabel(loc: TrainingCentreLocationRow): string {
  const n = (loc.name || '').trim();
  const h = trainingCentreLocationHierarchyLine(loc);
  if (n && h) return `${n} — ${h}`;
  if (n) return n;
  return h;
}

/** Badge palette aligned with the INDMO prototype (Accredited / Licensed / other). */
export type AccreditationVisualStatus = 'accredited' | 'licensed' | 'muted';

export function resolveAccreditationVisualStatus(
  level:
    | {
        name_en?: string;
        name_tet?: string;
        name_pt?: string;
        roman_numeral?: string;
      }
    | null
    | undefined
): AccreditationVisualStatus {
  if (!level) return 'muted';
  const text = [level.name_en, level.name_tet, level.name_pt]
    .filter((s): s is string => typeof s === 'string' && Boolean(s.trim()))
    .join(' ')
    .trim()
    .toLowerCase();
  if (!text) return 'muted';
  if (/\bnon[-\s]?accredited\b/i.test(text)) return 'muted';
  if (/\blicensed\b/i.test(text)) return 'licensed';
  if (/\baccredited\b/i.test(text)) return 'accredited';
  return 'muted';
}

export interface TrainingCentreRow {
  id: number;
  name: string;
  kodigu: string;
  /** Public website URL when set in the CMS (may be absent on cached rows until sync). */
  website?: string;
  /** Expanded HTML from the API. */
  description: string;
  accreditation_level: TrainingCentreAccreditationLevelRow | null;
  locations: TrainingCentreLocationRow[];
  programs: TrainingCentreProgramLinkRow[];
  /** Wagtail mirror page for this training centre in the current locale (when present). */
  detail_page_id?: number;
  detail_url_path?: string;
}

/** Unique programs across training centres, sorted by name (for listings and Dexie cache). */
export function uniqueProgramsFromTrainingCentres(items: TrainingCentreRow[]): ProgramRow[] {
  const byId = new Map<number, ProgramRow>();
  for (const centre of items) {
    for (const link of centre.programs ?? []) {
      const p = link.program;
      if (!p) continue;
      const existing = byId.get(p.id);
      if (!existing) {
        byId.set(p.id, { ...p });
        continue;
      }
      if (!existing.detail_page_id && p.detail_page_id) {
        existing.detail_page_id = p.detail_page_id;
        existing.detail_url_path = p.detail_url_path;
      }
      if (!existing.certificate_level && p.certificate_level) {
        existing.certificate_level = p.certificate_level;
      }
      if (
        (!existing.competencies || existing.competencies.length === 0) &&
        p.competencies &&
        p.competencies.length > 0
      ) {
        existing.competencies = p.competencies;
      }
    }
  }
  return [...byId.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Training centres (directory rows) that list the given program id in their program links. */
export function trainingCentresOfferingProgram(
  programId: number,
  centres: TrainingCentreRow[]
): TrainingCentreRow[] {
  const out: TrainingCentreRow[] = [];
  for (const centre of centres) {
    const offers = (centre.programs ?? []).some((link) => link.program?.id === programId);
    if (offers) {
      out.push(centre);
    }
  }
  return [...out].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Payload aligned with GET /api/v2/public-programs/<id>/ for offline rendering from Dexie. */
export type OfflineProgramDetailPayload = {
  id: number;
  name: string;
  kodigu: string;
  certificate_level: ProgramLevelRow | null;
  competencies: ProgramCompetencyRow[];
  organisations: TrainingCentreRow[];
  detail_page_id?: number;
  detail_url_path?: string;
};

/** Build program detail from IndexedDB when the detail GET is unavailable (matches API shape). */
export async function buildOfflineProgramDetail(
  programId: number
): Promise<OfflineProgramDetailPayload | null> {
  let row: ProgramRow | undefined;
  try {
    row = await trainingCentresDb.program.get(programId);
  } catch {
    return null;
  }
  if (!row) return null;

  let centres: TrainingCentreRow[] = [];
  try {
    centres = await trainingCentresDb.trainingCentre.toArray();
  } catch {
    return null;
  }

  const organisations = trainingCentresOfferingProgram(programId, centres);
  const competencies = Array.isArray(row.competencies) ? row.competencies : [];

  return {
    id: row.id,
    name: row.name,
    kodigu: row.kodigu,
    certificate_level: row.certificate_level ?? null,
    competencies,
    organisations,
    detail_page_id: row.detail_page_id,
    detail_url_path: row.detail_url_path,
  };
}

export interface TrainingCentresMetaRow {
  id: 'default';
  etag: string | null;
  fetchedAt: number;
  totalCount: number;
}

export class TrainingCentresDb extends Dexie {
  trainingCentre!: EntityTable<TrainingCentreRow, 'id'>;
  program!: EntityTable<ProgramRow, 'id'>;
  meta!: EntityTable<TrainingCentresMetaRow, 'id'>;

  constructor() {
    super('TrainingCentresDb');
    this.version(1).stores({
      trainingCentre: 'id',
      program: 'id',
      meta: 'id',
    });
  }
}

export const trainingCentresDb = new TrainingCentresDb();
